import { useCallback, useEffect, useRef, useState } from "react";
import { getSpeechLang } from "./detectTextInfo";

export function useSpeechSynthesis() {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const queueRef = useRef<SpeechSynthesisUtterance | null>(null);

  const stop = useCallback(() => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  }, []);

  const speak = useCallback(
    async (
      text: string,
      options?: {
        lang?: string;
        rate?: number;
        pitch?: number;
        volume?: number;
      }
    ) => {
      if (!text?.trim() || !window.speechSynthesis) return;

      // stop current speech
      window.speechSynthesis.cancel();
      setIsSpeaking(false);

      // IMPORTANT: wait one tick (fixes Chrome/Safari bug)
      await new Promise((r) => setTimeout(r, 50));

      const utterance = new SpeechSynthesisUtterance(text);

      utterance.lang = options?.lang || getSpeechLang(text);
      utterance.rate = options?.rate ?? 1;
      utterance.pitch = options?.pitch ?? 1;
      utterance.volume = options?.volume ?? 1;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      queueRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    },
    []
  );

  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  return { speak, stop, isSpeaking };
}